import React, { useEffect, useRef, useState } from "react";
import { FileUp } from "lucide-react";
import { Button } from "../../Franchise/D2CDashboard/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../Franchise/D2CDashboard/ui/dialog";
import { Input } from "../../Franchise/D2CDashboard/ui/input";
import { Callout, Label } from "../../Franchise/D2CDashboard/ui/misc";
import {
  createVendorPolicyUploadUrl,
  recordVendorPolicy,
  uploadVendorPolicyPdf,
} from "../common/warrantyApi";
import { SAVE_FAILURE, pdfFileMessage, planLabel, policyNumberMessage, saveVendorPolicy } from "./vendorPolicy";

const API = {
  createUploadUrl: createVendorPolicyUploadUrl,
  uploadPdf: uploadVendorPolicyPdf,
  recordPolicy: recordVendorPolicy,
};

const FieldError = ({ id, children }) =>
  children ? (
    <span id={id} className="tw-text-xs tw-text-red-700">
      {children}
    </span>
  ) : null;

// Add the vendor policy of one subscription: the policy number and the vendor's PDF. Save asks the
// OMS for an upload link, puts the PDF there and records the number; the warranty service then
// marks the covers active and sends the PDF to the rider. `item` is the list row, or null when closed.
const AddVendorPolicyDialog = ({ item, accessToken, onClose, onSaved, onAlreadyIssued }) => {
  const [number, setNumber] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({ number: "", file: "" });
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  // A finished upload of the chosen file, kept when the save stopped after it, so Save again does
  // not upload the same PDF twice; and the record's request id, kept across a retry.
  const uploaded = useRef(null);
  const requestRef = useRef(null);
  const mounted = useRef(true);

  const subscriptionId = item?.subscriptionId;
  useEffect(() => {
    setNumber("");
    setFile(null);
    setErrors({ number: "", file: "" });
    setFailure("");
    setSaving(false);
    uploaded.current = null;
    requestRef.current = null;
  }, [subscriptionId]);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const next = { number: policyNumberMessage(number), file: pdfFileMessage(file) };
    setErrors(next);
    setFailure("");
    if (next.number || next.file) return;
    setSaving(true);
    const result = await saveVendorPolicy(API, {
      token: accessToken,
      subscriptionId,
      policyNumber: number,
      file,
      uploaded: uploaded.current,
      requestRef,
    });
    if (!mounted.current) return;
    setSaving(false);
    uploaded.current = result.uploaded;
    if (result.ok) {
      onSaved(item, result.data);
    } else if (result.kind === SAVE_FAILURE.ALREADY_ISSUED) {
      onAlreadyIssued(item);
    } else {
      setFailure(result.message);
    }
  };

  return (
    <Dialog open={Boolean(item)} onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="tw-max-w-lg">
        <DialogHeader>
          <DialogTitle>Add vendor policy</DialogTitle>
          <DialogDescription>
            {item ? `${item.frameNumber || "-"} · ${planLabel(item)}. ` : ""}
            One policy covers both theft and roadside. Once saved, the covers become active and the rider
            is sent the PDF.
          </DialogDescription>
        </DialogHeader>
        <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={save} noValidate>
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <Label htmlFor="vendorPolicyNumber">Policy number</Label>
            <Input
              id="vendorPolicyNumber"
              value={number}
              maxLength={80}
              autoComplete="off"
              invalid={Boolean(errors.number)}
              aria-describedby={errors.number ? "vendorPolicyNumberError" : undefined}
              disabled={saving}
              onChange={(event) => {
                setNumber(event.target.value);
                if (errors.number) setErrors((prev) => ({ ...prev, number: "" }));
              }}
            />
            <FieldError id="vendorPolicyNumberError">{errors.number}</FieldError>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <Label htmlFor="vendorPolicyPdf">Vendor's PDF</Label>
            <Input
              id="vendorPolicyPdf"
              type="file"
              accept="application/pdf,.pdf"
              invalid={Boolean(errors.file)}
              aria-describedby={errors.file ? "vendorPolicyPdfError" : "vendorPolicyPdfHint"}
              disabled={saving}
              className="tw-h-auto tw-py-1.5"
              onChange={(event) => {
                setFile(event.target.files?.[0] || null);
                if (errors.file) setErrors((prev) => ({ ...prev, file: "" }));
              }}
            />
            {errors.file ? (
              <FieldError id="vendorPolicyPdfError">{errors.file}</FieldError>
            ) : (
              <span id="vendorPolicyPdfHint" className="tw-text-xs tw-text-muted-foreground">
                The PDF the vendor sent, at most 10 MB.
              </span>
            )}
          </div>
          {failure && (
            <Callout tone="danger" role="alert">
              {failure}
            </Callout>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              <FileUp aria-hidden="true" />
              {saving ? "Saving" : "Save policy"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddVendorPolicyDialog;
